import { vars } from '@etg/wings';
import { css, cx, resetButtonStyle } from '@eti/styles';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import type { DayEventHandler } from 'react-day-picker';
import { DayPicker, UI } from 'react-day-picker';
import { useDirection, useProperty, useSiteContext } from '@eti/providers';
import type { BestPricePerDay } from '@eti/schema-types';
import type { Locale } from 'date-fns';
import { startOfMonth } from 'date-fns';
import 'react-day-picker/src/style.css';
import { tripTypes } from '../../../../constants/tripTypesConstants';
import {
  calculateDayRange,
  getOneYearFromToday,
  isDisabled,
  isMasked,
  isSelected,
} from '../../utils/calendarDateUtils';
import { formatWeekdayName, getLocale } from '../../utils/calendarLocales';
import { type DEPARTURE_DATE, RETURN_DATE } from '../../constants/formFields';
import { getFirstAndLastSelectableDateButtons } from '../../utils/getFirstAndLastSelectableDateButtons';
import CalendarFooter from './CalendarFooter';
import CustomDayWithPriceIndicator from './CustomDayWithPriceIndicator';

const calendarContainerStyle = css`
  background-color: #fff;
  border: 1px solid ${vars.colors.inputs.border};
  box-shadow:
    0 10px 20px rgb(0 0 0 / 4%),
    0 2px 6px rgb(0 0 0 / 4%),
    0 0 1px rgb(0 0 0 / 4%);
`;

const classNames = {
  [UI.Root]: cx(
    'rdp-root',
    css`
      margin: 0.5rem 1rem;
    `,
  ),
  [UI.CaptionLabel]: css`
    align-self: center;
    color: ${vars.colors.text.primary};
    flex: 1;
    font-size: 0.875rem;
    font-weight: 500;
    text-align: center;
  `,
  [UI.Nav]: css`
    display: flex;
    justify-content: space-between;
    position: absolute;
    width: 100%;
  `,
  [UI.NextMonthButton]: css`
    ${resetButtonStyle};
    align-items: center;
    display: flex;
    height: var(--rdp-nav-height);
    justify-content: center;
    width: var(--rdp-nav-height);

    &[aria-disabled='true'] {
      cursor: auto;
      /* stylelint-disable-next-line */
      opacity: var(--rdp-nav_button-disabled-opacity);
    }

    &[aria-disabled='true']&:focus,
    &[aria-disabled='true']&:focus-visible {
      outline: none;
    }

    &:focus,
    &:focus-visible {
      background-color: unset;
      border: none;
      outline: 2px solid ${vars.colors.focusRing};
      outline-width: 2px;
    }
  `,
  [UI.PreviousMonthButton]: css`
    ${resetButtonStyle};
    align-items: center;
    display: flex;
    height: var(--rdp-nav-height);
    justify-content: center;
    width: var(--rdp-nav-height);

    &[aria-disabled='true'] {
      cursor: auto;
      /* stylelint-disable-next-line */
      opacity: var(--rdp-nav_button-disabled-opacity);
    }

    &[aria-disabled='true']&:focus,
    &[aria-disabled='true']&:focus-visible {
      outline: none;
    }

    &:focus,
    &:focus-visible {
      background-color: unset;
      border: none;
      outline: 2px solid ${vars.colors.focusRing};
      outline-width: 2px;
    }
  `,
  [UI.Chevron]: css`
    height: 22px;
    width: 22px;
  `,
  [UI.MonthGrid]: css`
    margin-top: 8px;
    width: 100%;
  `,
  [UI.Months]: css`
    width: 100%;
  `,
  [UI.Weekday]: css`
    color: #737373;
    font-weight: 400;
    padding-bottom: 4px;
    text-transform: unset;
  `,
  [UI.Day]: cx(
    'notranslate',
    'rdp-day',
    css`
      &:focus,
      &:focus-visible {
        outline: 2px solid;
      }
    `,
  ),
  [UI.DayButton]: css`
    ${resetButtonStyle};
    height: 100%;
    position: relative;
    width: 100%;

    .rdp-selected && {
      background-color: ${vars.colors.inputs.main};
      border-radius: inherit;
      font-size: 0.875rem;
    }

    .rdp-masked && {
      background-color: ${vars.colors.inputs.selected};
    }

    &:hover {
      background-color: ${vars.colors.inputs.hover};
    }

    &[disabled] {
      cursor: auto;
    }

    &[disabled]:hover {
      background-color: unset;
    }

    &:focus,
    &:focus-visible {
      background-color: unset;
      border: none;
      border-radius: 3px;
      outline: 2px solid ${vars.colors.focusRing};
      z-index: 999;
    }
  `,
};

const priceCalendarClassNames = {
  ...classNames,
  [UI.Root]: cx(
    classNames[UI.Root],
    css`
      margin: 0.5rem 0.25rem;
    `,
  ),
  [UI.MonthGrid]: cx(
    classNames[UI.MonthGrid],
    css`
      border-collapse: separate;
      border-spacing: 4px;
    `,
  ),
  [UI.DayButton]: cx(
    classNames[UI.DayButton],
    css`
      background-color: #fafafa;
      border: 2px solid #fff;
      border-radius: 5px;

      &[disabled]:hover {
        background-color: #fafafa;
      }

      &:focus,
      &:focus-visible {
        border: 2px solid ${vars.colors.inputs.focus};
        outline: none;
      }
    `,
  ),
};

export interface CalendarProps {
  bestPricesPerDay?: BestPricePerDay[];
  range?: { departureDate?: Date; returnDate?: Date };
  currentField?: typeof DEPARTURE_DATE | typeof RETURN_DATE | null;
  languageCode?: string;
  loading?: boolean;
  daysFromTodayForValidDepartureDate?: number;
  previousBoundDepartureDate?: Date;
  selectedTripType?: string;
  onDepartureNewDateChange?: (day: Date) => void;
  onReturnNewDateChange?: (day: Date) => void;
  onDepartureDateChange?: (day: string) => void;
  onReturnDateChange?: (day: string) => void;
}

const Calendar = ({
  bestPricesPerDay,
  range = {},
  currentField,
  daysFromTodayForValidDepartureDate,
  loading = false,
  previousBoundDepartureDate,
  selectedTripType,
  onDepartureDateChange,
  onReturnDateChange,
  onDepartureNewDateChange,
  onReturnNewDateChange,
}: CalendarProps) => {
  const { direction } = useDirection();

  const { language } = useSiteContext();

  const { p } = useProperty();

  const isPriceCalendarEnabled = p('IbeClient.Price.Calendar.Enabled');

  const [locale, setLocale] = useState<Locale>();

  const getDefaultMonth = () => {
    if (!currentField) {
      return new Date();
    }

    const { departureDate } = range;
    const selectedDate = range[currentField];

    if (selectedDate && !Number.isNaN(selectedDate.getTime())) {
      return selectedDate;
    }

    if (currentField === RETURN_DATE && departureDate && !Number.isNaN(departureDate.getTime())) {
      return departureDate;
    }

    if (previousBoundDepartureDate) {
      return previousBoundDepartureDate;
    }

    return new Date();
  };

  useEffect(() => {
    const loadLocal = async () => {
      if (language?.locale) {
        const tempLocale = await getLocale(language.locale);
        setLocale(tempLocale);
      }
    };
    loadLocal();
  }, [language?.locale]);

  useEffect(() => {
    if (!currentField) {
      return;
    }

    const dateButtons = getFirstAndLastSelectableDateButtons(range);

    dateButtons[currentField]?.focus();
  }, [currentField, range]);

  const handleDayClick: DayEventHandler<MouseEvent> = (day, { disabled }) => {
    if (!disabled) {
      calculateDayRange(
        currentField,
        range.departureDate,
        range.returnDate,
        day,
        onDepartureDateChange || onDepartureNewDateChange,
        onReturnDateChange || onReturnNewDateChange,
      );
    }
  };

  return (
    <div className={calendarContainerStyle}>
      <DayPicker
        classNames={isPriceCalendarEnabled ? priceCalendarClassNames : classNames}
        components={{
          ...(isPriceCalendarEnabled
            ? {
                DayButton: ({ day, modifiers, ...buttonProps }) => (
                  <CustomDayWithPriceIndicator
                    date={day.date}
                    loading={loading}
                    modifiers={modifiers}
                    priceCalendarData={bestPricesPerDay ?? undefined}
                    {...buttonProps}
                  />
                ),
              }
            : {}),
        }}
        defaultMonth={getDefaultMonth()}
        dir={direction}
        endMonth={getOneYearFromToday()}
        formatters={{ formatWeekdayName }}
        locale={locale}
        modifiers={{
          selected: (day) => isSelected(day, range.departureDate, range.returnDate),
          disabled: (day) =>
            isDisabled(
              day,
              daysFromTodayForValidDepartureDate,
              previousBoundDepartureDate,
              selectedTripType,
            ),
          masked: (day) => isMasked(day, selectedTripType, range.departureDate, range.returnDate),
        }}
        modifiersClassNames={{
          masked: 'rdp-masked',
        }}
        modifiersStyles={{
          disabled: {
            color: '#737373',
            cursor: 'default',
            opacity: isPriceCalendarEnabled ? 1 : 'var(--rdp-disabled-opacity)',
          },
          selected: {
            color: '#fff',
            borderRadius: isPriceCalendarEnabled ? '5px' : '3px',
            outlineOffset: '0',
          },
          today: {
            color: vars.colors.text.primary,
          },
        }}
        onDayClick={handleDayClick}
        startMonth={startOfMonth(new Date())}
      />
      {selectedTripType === tripTypes.RETURN && (range.departureDate || range.returnDate) && (
        <CalendarFooter {...range} />
      )}
    </div>
  );
};

export default Calendar;
